import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { notificationService } from '../services/api';
import { useTheme, THEMES } from '../context/ThemeContext';
import { FiMenu, FiX, FiChevronDown, FiBell, FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { theme, changeTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const isRTL = i18n.language === 'ar';

  const toggleLanguage = () => {
    i18n.changeLanguage(isRTL ? 'en' : 'ar');
  };

  // Transparent navbar on homepage (not logged in)
  const isHomePage = location.pathname === '/' && !user;

  useEffect(() => {
    if (!isHomePage) { setScrolled(true); return; }
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // جلب عدد الإشعارات غير المقروءة
  useEffect(() => {
    if (!user) return;
    let retryDelay = 300000; // يبدأ بـ 5 دقائق
    let timer;
    const fetchUnread = async () => {
      try {
        const { data } = await notificationService.getUnreadCount();
        setUnreadCount(data?.unreadCount || 0);
        retryDelay = 300000; // إعادة تعيين الفاصل الزمني عند النجاح
      } catch (err) {
        if (err?.response?.status === 401) {
          return; // توقف عن الاستقصاء — الجلسة منتهية
        }
        if (err?.response?.status === 429) {
          retryDelay = Math.min(retryDelay * 2, 1800000); // backoff حتى 30 دقيقة
        }
      } finally {
        timer = setTimeout(fetchUnread, retryDelay);
      }
    };
    fetchUnread();
    return () => clearTimeout(timer);
  }, [user]);

  const openNotifications = async () => {
    setIsNotifOpen(!isNotifOpen);
    setIsProfileOpen(false);
    setIsThemeOpen(false);
    if (!isNotifOpen) {
      try {
        const { data } = await notificationService.getAll({ limit: 8 });
        setNotifications(data?.notifications || []);
      } catch {}
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {}
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className={`text-white p-4 sticky top-0 z-50 transition-all duration-500 ${isHomePage ? (scrolled ? 'navbar-solid' : 'navbar-transparent') : `bg-gradient-to-r ${theme.navFrom} ${theme.navTo} shadow-lg`}`}>
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold flex items-center gap-2 hover:scale-105 transition-transform">
          <img src="/logo.png" alt="SOLOU" className="h-9 w-9 rounded-lg object-cover" />
          <span>SOLOU</span>
        </Link>

        {/* سطح المكتب - القائمة الرئيسية */}
        {user ? (
          <div className="hidden md:flex items-center gap-1">
            <Link to={user?.organization?.type === 'salon' ? '/salon' : user?.organization?.type === 'tailoring' ? '/tailoring-dashboard' : '/dashboard'} className={`${theme.btnHover} px-3 py-2 rounded transition`}>
              {t('navbar.dashboard')}
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="hover:bg-red-500 px-3 py-2 rounded transition font-semibold">
                🔐 {t('navbar.admin')}
              </Link>
            )}
          </div>
        ) : (
          <div className="hidden md:flex items-center gap-6">
            {[
              { label: t('navbar.business'), to: '/register', icon: '💼' },
              { label: t('navbar.healthcare'), to: '/healthcare', icon: '🏥' },
              { label: t('navbar.salons'), to: '/salons', icon: '💇' },
              { label: t('navbar.tailoring'), to: '/tailoring', icon: '🧵' },
              { label: t('navbar.cashier'), to: '/cashier-landing', icon: '💰' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.to}
                className="relative font-semibold text-sm text-white/80 hover:text-white transition-all duration-300 py-1 group"
              >
                <span className="hidden lg:inline ml-1">{item.icon}</span>
                {item.label}
                <span className="absolute bottom-0 right-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300 rounded-full"></span>
              </Link>
            ))}
          </div>
        )}

        {/* أزرار المستخدم + محوّل الألوان */}
        <div className="hidden md:flex items-center gap-3">
          {/* � تغيير اللغة */}
          <button
            onClick={toggleLanguage}
            className={`${theme.btnHover} px-3 py-2 rounded transition text-sm font-bold flex items-center gap-1.5`}
            title={isRTL ? 'Switch to English' : 'Switch to Arabic'}
          >
            <FiGlobe size={16} />
            {isRTL ? 'EN' : 'ع'}
          </button>

          {/* �🎨 محوّل الألوان */}
          <div className="relative">
            <button
              onClick={() => { setIsThemeOpen(!isThemeOpen); setIsProfileOpen(false); }}
              className={`${theme.btnHover} px-3 py-2 rounded transition text-lg`}
              title={t('navbar.changeTheme')}
            >
              🎨
            </button>
            {isThemeOpen && (
              <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-52 bg-white text-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-200 z-50`}>
                <div className="px-4 py-2.5 bg-gray-50 border-b font-bold text-sm text-gray-600">
                  {t('navbar.chooseTheme')}
                </div>
                {Object.values(THEMES).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { changeTheme(t.id); setIsThemeOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 transition text-sm ${
                      theme.id === t.id ? 'bg-gray-100 font-bold' : ''
                    }`}
                  >
                    <span className="text-xl">{t.emoji}</span>
                    <span>{t.name}</span>
                    {theme.id === t.id && <span className="mr-auto text-green-500">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 🔔 جرس الإشعارات */}
          {user && (
            <div className="relative">
              <button
                onClick={openNotifications}
                className={`${theme.btnHover} px-3 py-2 rounded transition text-lg relative`}
                title={t('navbar.notifications')}
              >
                <FiBell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {isNotifOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-80 bg-white text-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-200 z-50 max-h-96 flex flex-col`}>
                  <div className="px-4 py-2.5 bg-gray-50 border-b font-bold text-sm text-gray-600 flex justify-between items-center">
                    <span>{t('navbar.notifications')}</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-blue-500 text-xs hover:underline">
                        {t('navbar.markAllRead')}
                      </button>
                    )}
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <p className="text-center text-gray-400 py-8 text-sm">{t('navbar.noNotifications')}</p>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n._id}
                          className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer ${
                            !n.isRead ? 'bg-blue-50/50' : ''
                          }`}
                          onClick={() => {
                            if (n.actionUrl) navigate(n.actionUrl);
                            setIsNotifOpen(false);
                          }}
                        >
                          <p className="font-semibold text-sm text-gray-800">{n.title}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(n.createdAt).toLocaleDateString('ar-SA')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {user ? (
            <div className="relative">
              <button
                onClick={() => { setIsProfileOpen(!isProfileOpen); setIsThemeOpen(false); }}
                className={`flex items-center gap-2 ${theme.btnHover} px-3 py-2 rounded transition`}
              >
                <span>{user.firstName}</span>
                <FiChevronDown />
              </button>

              {isProfileOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 bg-white text-gray-900 rounded-lg shadow-lg overflow-hidden`}>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    {t('navbar.profile')}
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    {t('navbar.settings')}
                  </Link>
                  <Link
                    to="/subscription"
                    className="block px-4 py-2 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    💳 {t('navbar.subscription')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 hover:bg-gray-100 text-red-600`}
                  >
                    {t('navbar.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-3 items-center">
              <Link to="/login" className="text-white/80 hover:text-white font-medium text-sm transition-colors px-3 py-2">
                {t('navbar.login')}
              </Link>
              <Link to="/register" className="bg-blue-500 hover:bg-blue-400 px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 hover:shadow-blue-400/30 transition-all hover:scale-105">
                {t('navbar.createFreeAccount')}
              </Link>
            </div>
          )}
        </div>

        {/* الموبايل - القائمة المنسدلة */}
        <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* قائمة الموبايل */}
      {isOpen && (
        <div className="md:hidden mt-4 space-y-2 pb-4">
          {/* محوّل الألوان للموبايل */}
          <div className="flex gap-2 px-4 py-2 flex-wrap">
            {Object.values(THEMES).map((t) => (
              <button
                key={t.id}
                onClick={() => changeTheme(t.id)}
                className={`text-xl p-1.5 rounded-lg transition ${
                  theme.id === t.id ? 'bg-white/30 scale-110' : 'hover:bg-white/10'
                }`}
                title={t.name}
              >
                {t.emoji}
              </button>
            ))}
          </div>
          <div className="border-t border-white/20 my-2"></div>

          {user ? (
            <>
              <Link
                to={user?.organization?.type === 'salon' ? '/salon' : user?.organization?.type === 'tailoring' ? '/tailoring-dashboard' : '/dashboard'}
                className={`block px-4 py-2 ${theme.btnHover} rounded`}
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.dashboard')}
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="block px-4 py-2 hover:bg-red-600 rounded font-semibold"
                  onClick={() => setIsOpen(false)}
                >
                  🔐 {t('navbar.adminPanel')}
                </Link>
              )}
              <Link
                to="/profile"
                className={`block px-4 py-2 ${theme.btnHover} rounded`}
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.profile')}
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-red-600 rounded"
              >
                {t('navbar.logout')}
              </button>
            </>
          ) : (
            <>
              <div className="px-4 py-2 text-white/50 text-xs font-bold uppercase tracking-wider">{t('navbar.solutions')}</div>
              <Link to="/register" className={`block px-4 py-2 ${theme.btnHover} rounded`} onClick={() => setIsOpen(false)}>
                💼 {t('navbar.business')}
              </Link>
              <Link to="/healthcare" className={`block px-4 py-2 ${theme.btnHover} rounded`} onClick={() => setIsOpen(false)}>
                🏥 {t('navbar.healthcare')}
              </Link>
              <Link to="/salons" className={`block px-4 py-2 ${theme.btnHover} rounded`} onClick={() => setIsOpen(false)}>
                💇 {t('navbar.salons')}
              </Link>
              <Link to="/tailoring" className={`block px-4 py-2 ${theme.btnHover} rounded`} onClick={() => setIsOpen(false)}>
                🧵 {t('navbar.tailoring')}
              </Link>
              <Link to="/cashier-landing" className={`block px-4 py-2 ${theme.btnHover} rounded`} onClick={() => setIsOpen(false)}>
                💰 {t('navbar.cashier')}
              </Link>
              <div className="border-t border-white/20 my-2"></div>
              {/* Language toggle for mobile */}
              <button
                onClick={toggleLanguage}
                className={`block w-full text-left px-4 py-2 ${theme.btnHover} rounded flex items-center gap-2`}
              >
                <FiGlobe size={16} /> {isRTL ? 'English' : 'العربية'}
              </button>
              <div className="border-t border-white/20 my-2"></div>
              <Link
                to="/login"
                className={`block px-4 py-2 ${theme.btnHover} rounded`}
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.login')}
              </Link>
              <Link
                to="/register"
                className="block px-4 py-2 bg-green-500 hover:bg-green-600 rounded"
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.createAccount')}
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
